// Pipe Take-off — new project. Three-step flow:
//   1. POST /api/storage/sas-url to mint a write-only SAS for the upload
//   2. PUT the file to that SAS URL with the right Azure Blob headers
//   3. POST /api/tools/pipe-takeoff/projects with metadata + the blobPath
// Then optionally POST /:id/extract and redirect to the progress page.
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { api } from "../../../lib/api";

export function NewProject() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [drawingNumber, setDrawingNumber] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [autoExtract, setAutoExtract] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError("Pick a PDF first");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const sas = await api<{ blobPath: string; uploadUrl: string }>(
        "/api/storage/sas-url",
        {
          method: "POST",
          json: {
            toolId: "pipe-takeoff",
            filename: file.name,
            contentType: file.type || "application/pdf",
          },
        },
      );

      const putRes = await fetch(sas.uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/pdf",
        },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Blob upload failed: ${putRes.status} ${putRes.statusText}`);
      }

      const project = await api<{ id: number }>(
        "/api/tools/pipe-takeoff/projects",
        {
          method: "POST",
          json: {
            name,
            description: description || undefined,
            drawingNumber: drawingNumber || undefined,
            pdfBlobPath: sas.blobPath,
            pdfFilename: file.name,
          },
        },
      );

      if (autoExtract) {
        await api(
          `/api/tools/pipe-takeoff/projects/${project.id}/extract`,
          { method: "POST" },
        );
        navigate(`/tools/pipe-takeoff/${project.id}/extract`);
      } else {
        navigate(`/tools/pipe-takeoff/${project.id}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="bsp-page-header">
        <h1 className="bsp-page-title">New take-off</h1>
      </div>

      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)", marginBottom: 16 }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}

      <form onSubmit={submit} className="bsp-card" style={{ display: "grid", gap: 16, maxWidth: 720 }}>
        <div>
          <div className="bsp-label">Project name</div>
          <input
            className="bsp-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={busy}
            placeholder="e.g. Site B fire main 2026"
          />
        </div>

        <div>
          <div className="bsp-label">Description (optional)</div>
          <input
            className="bsp-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
          />
        </div>

        <div>
          <div className="bsp-label">Drawing number (optional)</div>
          <input
            className="bsp-input"
            value={drawingNumber}
            onChange={(e) => setDrawingNumber(e.target.value)}
            disabled={busy}
            placeholder="e.g. PIP-1234-001"
          />
        </div>

        <div>
          <div className="bsp-label">PDF</div>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={busy}
            required
          />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={autoExtract}
            onChange={(e) => setAutoExtract(e.target.checked)}
            disabled={busy}
          />
          <span>Start extraction immediately</span>
        </label>

        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" className="bsp-btn bsp-btn-primary" disabled={busy}>
            {busy ? "Uploading…" : "Upload and create"}
          </button>
          <button
            type="button"
            className="bsp-btn bsp-btn-secondary"
            onClick={() => navigate("/tools/pipe-takeoff")}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}
